import {
  type AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import {
  destinationPoint,
  type GeoPoint,
  metersPerPixelAt,
  velocityArrowPath,
} from 'src/app/utils';

/**
 * One party's live-location marker on the map (AxisLink dual-party view).
 * `features` uses the same `{ count, rows: [{ gps: { latitude, longitude } }] }`
 * shape returned by `UserService.getLocations`.
 */
export interface GoogleMapParty {
  label: string;
  features: any;
  /** Colour of this party's dot marker and FR-402 velocity arrow; defaults to a neutral gray if omitted. */
  color?: string;
  partyType?: 'SURVIVOR' | 'PERPETRATOR' | string;
}

export interface GoogleMapPartyDragEvent {
  partyType?: 'SURVIVOR' | 'PERPETRATOR' | string;
  partyLabel: string;
  lat: number;
  lng: number;
}

/**
 * AxisLink proximity zone overlay (BFRD FR-405): the Yellow (warning) and Red (emergency)
 * radii, in meters, both centred on the Protected Party's latest reported location.
 * Optional whiteRadiusMeters draws an outer monitoring ring (typically 2× yellow).
 */
export interface GoogleMapZoneConfig {
  center: google.maps.LatLngLiteral;
  yellowRadiusMeters: number;
  redRadiusMeters: number;
  /** Outer monitoring ring — typically 2× yellow. */
  whiteRadiusMeters?: number;
}

/** A party's FR-402 velocity arrow, drawn as a bold line over a white casing so it stays legible. */
interface GoogleMapArrow {
  /** Stable track key so `@for` recreates polylines when the shaft moves. */
  id: string;
  path: GeoPoint[];
  casingOptions: google.maps.PolylineOptions;
  lineOptions: google.maps.PolylineOptions;
}

/** Cached track used to rebuild velocity arrows when zoom changes without re-parsing features. */
interface PartyArrowSource {
  track: GeoPoint[];
  color?: string;
}

/** A party's recent-movement trail — the faint line the marker has travelled along. */
interface GoogleMapTrail {
  path: GeoPoint[];
  options: google.maps.PolylineOptions;
}

const DEFAULT_ARROW_COLOR = '#495057';
const DEFAULT_TRAIL_COLOR = '#6c757d';
/** Most recent points kept for a trail — bounds how many polyline segments a long real history renders. */
const TRAIL_MAX_POINTS = 12;
const TRAIL_MIN_OPACITY = 0.1;
const TRAIL_MAX_OPACITY = 0.45;
/** Re-measure once layout settles — the map is often created before its container has a size. */
const DEFERRED_FIT_DELAY_MS = 400;
const FIT_PADDING_PX = 28;
/**
 * Max on-screen shaft length for FR-402 velocity arrows (px). Length is chosen from speed in
 * pixel space, then converted to meters at the live zoom so zoom in/out does not change how
 * long the arrow looks next to fixed-pixel dot markers. Sized so the tip clears the dot marker
 * (markers sit above polylines in Google Maps' marker pane).
 */
const ARROW_MAX_LENGTH_PX = 32;
/** Fallback meters-per-pixel when the map zoom/lat is not ready yet (~zoom 13 mid-latitudes). */
const ARROW_FALLBACK_METERS_PER_PIXEL = 19;
/** Dot marker diameter, in px (party mode). */
const DOT_MARKER_DIAMETER_PX = 14;
const DOT_MARKER_LABEL_HEIGHT_PX = 16;
/** Rough per-character width (px) for the label chip's bold 9px text — an SVG icon can't measure real text before being built, so this is a generous estimate, not exact. */
const DOT_MARKER_CHAR_WIDTH_PX = 6.2;
const DOT_MARKER_CHIP_PADDING_X_PX = 6;

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
  standalone: false,
})
export class GoogleMapComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(GoogleMap) mapRef?: GoogleMap;

  @Input() width: string = '100%';
  @Input() height!: string;
  @Input() features: any;
  @Input() customMarkers: any;
  /**
   * Optional dual/multi-party mode: when provided (non-empty), renders each
   * party's latest known location instead of the single-user tracking trail
   * driven by `features`/`customMarkers`. Existing single-user callers are
   * unaffected since they never set this input.
   */
  @Input() parties?: GoogleMapParty[];
  /**
   * AxisLink proximity zone overlay (BFRD FR-405): two concentric circles, centred on the
   * Protected Party's latest reported location, showing the configured Yellow (warning) and
   * Red (emergency) radii. Optional and independent of `parties`/`features` mode.
   */
  @Input() zones?: GoogleMapZoneConfig | null;
  /**
   * Optional Google Maps JS style array (e.g. a dark/night basemap) — passed straight through as
   * `google.maps.MapOptions.styles`. Kept out of the component's own hardcoded options so the
   * default (light, single-user) callers are unaffected.
   */
  @Input() mapStyles?: google.maps.MapTypeStyle[];
  /**
   * Opt in to a chrome-free map: Google's own Map/Satellite toggle, fullscreen, Street View, and
   * zoom/rotate controls are all dropped. Callers that draw their own overlay buttons (which
   * default to the same corners as several of these) set this explicitly and unconditionally —
   * it's deliberately *not* inferred from `parties`/`mapStyles` truthiness, since those can change
   * asynchronously (e.g. `parties` starts empty until the first location fetch resolves) well
   * after the map is first created, and `GoogleMap` only applies `options` at creation time.
   */
  @Input() cleanChrome = false;
  /**
   * Initial map zoom, and the closest zoom dual-party `fitBounds` is allowed to settle on.
   * When parties/zones are spread out, the map still zooms out as needed so nothing is clipped.
   */
  @Input() zoom = 13;
  /**
   * Pixel padding around content when dual-party mode frames the viewport. Larger values leave
   * more margin (effectively more zoomed out) after the initial fit.
   */
  @Input() fitPaddingPx = FIT_PADDING_PX;
  /** Whether party markers on the map can be dragged (used for interactive simulation) */
  @Input() markersDraggable = false;
  /** Emits when a draggable party marker is dropped */
  @Output() partyDragEnd = new EventEmitter<GoogleMapPartyDragEvent>();

  /**
   * Memoized against `mapStyles`/`cleanChrome`'s own values: a plain getter returning a fresh
   * object literal would hand `<google-map [options]>` a new identity on every change-detection
   * tick, and `@angular/google-maps` calls the real `google.maps.Map#setOptions` whenever that
   * identity changes — i.e. every tick, continuously, pinning the CPU.
   */
  private cachedMapOptions: google.maps.MapOptions = {};
  private cachedMapStylesRef?: google.maps.MapTypeStyle[];
  private cachedCleanChrome = false;

  get mapOptions(): google.maps.MapOptions {
    if (
      this.cachedMapStylesRef !== this.mapStyles ||
      this.cachedCleanChrome !== this.cleanChrome
    ) {
      this.cachedMapStylesRef = this.mapStyles;
      this.cachedCleanChrome = this.cleanChrome;
      this.cachedMapOptions = {
        ...(this.mapStyles
          ? { styles: this.mapStyles }
          : // Without a custom style array, newer vector-rendered maps pick their basemap's
            // light/dark scheme from the OS/browser's own colour-scheme preference rather than
            // defaulting to light — pin it explicitly so an unstyled map (e.g. this component's
            // own light-mode toggle state) always renders the same regardless of the viewer's
            // system theme.
            { colorScheme: 'LIGHT' }),
        ...(this.cleanChrome
          ? {
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
              zoomControl: false,
              rotateControl: false,
              // The unified pan/zoom/tilt widget Google introduced separately from the legacy
              // `zoomControl`/`rotateControl` flags above — those alone don't hide it.
              cameraControl: false,
            }
          : {}),
      };
    }
    return this.cachedMapOptions;
  }

  // White is the outer monitoring perimeter (typically 2× yellow). Yellow stays a thin
  // threshold ring. Red is bolder/more filled by design: it's meant to read as urgent at a glance.
  whiteZoneCircleOptions: google.maps.CircleOptions = {
    strokeColor: '#d9d9da',
    strokeOpacity: 0.9,
    strokeWeight: 1.5,
    fillColor: '#d9d9da',
    fillOpacity: 0,
    clickable: false,
    zIndex: 0,
  };
  yellowZoneCircleOptions: google.maps.CircleOptions = {
    strokeColor: '#ffc107',
    strokeOpacity: 0.9,
    strokeWeight: 1.5,
    fillColor: '#ffc107',
    fillOpacity: 0.05,
    clickable: false,
    zIndex: 1,
  };
  redZoneCircleOptions: google.maps.CircleOptions = {
    strokeColor: '#dc3545',
    strokeOpacity: 0.95,
    strokeWeight: 2,
    fillColor: '#dc3545',
    fillOpacity: 0.22,
    clickable: false,
    zIndex: 2,
  };

  /** Party-mode dot+label markers are pushed in here too, alongside single-user pins. */
  markers: any[] = [];
  /** FR-402 velocity arrows, one per party that has moved measurably. */
  arrows: GoogleMapArrow[] = [];
  /** Dual-party mode: each party's recent-movement trail, from whatever history the API returned. */
  trails: GoogleMapTrail[] = [];
  center: any = { lat: 40.80779, lng: -73.291467 };
  vertices: google.maps.LatLngLiteral[] = [];
  /** Single-user mode: the travelled trail. */
  polylineOptions = {
    geodesic: true,
    strokeColor: '#3cb371',
    strokeOpacity: 0.7,
    strokeWeight: 4,
  };
  /**
   * Dual-party mode: a dashed connector tying the pair together. Deliberately distinct from the
   * solid trail above — this line is a relationship, not a path anyone travelled. Google Maps has
   * no dash option on polylines, so the dashes are repeated icons over a transparent stroke.
   */
  connectorPolylineOptions: google.maps.PolylineOptions = {
    geodesic: true,
    strokeColor: '#6c757d',
    strokeOpacity: 0,
    strokeWeight: 2,
    clickable: false,
    zIndex: 3,
    icons: [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeColor: '#6c757d',
          strokeOpacity: 0.75,
          strokeWeight: 2,
          scale: 3,
        },
        offset: '0',
        repeat: '12px',
      },
    ],
  };
  iconPath: string = '../../../../assets/img/';

  /** Fit the viewport to the content once; later refreshes must not yank the operator's pan/zoom. */
  private hasFitted = false;
  private deferredFitHandle: ReturnType<typeof setTimeout> | undefined;
  /** Party tracks kept so zoom changes can rescale arrows without a full feature re-parse. */
  private partyArrowSources: PartyArrowSource[] = [];
  private zoomListener?: google.maps.MapsEventListener;

  constructor(private ngZone: NgZone) {}

  get pathPolylineOptions(): google.maps.PolylineOptions {
    return this.parties?.length
      ? this.connectorPolylineOptions
      : this.polylineOptions;
  }

  ngOnInit() {
    this.rebuild();
  }

  /**
   * The panel polls for new positions and re-assigns `parties`/`zones` in place, so the overlays
   * have to be rebuilt on every change — otherwise the markers stay frozen at their first paint
   * while the zone rings (bound straight through to `<map-circle>`) keep moving (BFRD FR-406).
   */
  ngOnChanges() {
    this.rebuild();
    this.tryFitBounds();
    this.bindZoomListener();
  }

  ngAfterViewInit() {
    this.tryFitBounds();
    this.bindZoomListener();
    this.deferredFitHandle = setTimeout(
      () => this.tryFitBounds(),
      DEFERRED_FIT_DELAY_MS,
    );
  }

  ngOnDestroy() {
    if (this.deferredFitHandle) {
      clearTimeout(this.deferredFitHandle);
      this.deferredFitHandle = undefined;
    }
    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
      this.zoomListener = undefined;
    }
  }

  /**
   * Google Maps doesn't repaint its tiles on its own when its container changes size outside of
   * Angular's own `[height]`/`[width]` bindings (e.g. a CSS class toggling the container's layout,
   * such as entering/exiting the Fullscreen API) — the canvas is left stale/blank at the old size
   * until told to re-measure. Callers that resize the map's container from outside must call this
   * once layout has actually settled (e.g. a tick after a fullscreen transition).
   */
  triggerResize(): void {
    const map = this.mapRef?.googleMap;
    if (!map) {
      return;
    }
    google.maps.event.trigger(map, 'resize');
    map.setCenter(this.center);
  }

  private rebuild() {
    this.markers = [];
    this.arrows = [];
    this.trails = [];
    this.vertices = [];
    this.partyArrowSources = [];

    if (this.parties?.length) {
      this.addPartyMarkers();
    } else if (this.features) {
      this.addMarkers();
    }
  }

  /**
   * Google Maps fires `zoom_changed` outside Angular; re-enter the zone so the template picks up
   * the rebuilt polylines. Bound once per map instance.
   */
  private bindZoomListener(): void {
    const map = this.mapRef?.googleMap;
    if (!map || this.zoomListener) {
      return;
    }
    this.zoomListener = map.addListener('zoom_changed', () => {
      this.ngZone.run(() => this.rebuildArrows());
    });
  }

  /** Recompute FR-402 arrows at the current zoom without touching markers/trails/zones. */
  private rebuildArrows(): void {
    if (!this.partyArrowSources.length) {
      this.arrows = [];
      return;
    }
    const metersPerPixel = this.arrowMetersPerPixel();
    // Assign a new array so `@for` / map-polyline reliably recreate or update.
    this.arrows = this.partyArrowSources
      .map(({ track, color }, index) => {
        const arrowPath = velocityArrowPath(
          track,
          metersPerPixel,
          ARROW_MAX_LENGTH_PX,
        );
        if (!arrowPath) {
          return null;
        }
        return this.buildArrow(
          arrowPath,
          color,
          `${index}-${arrowPath[0].lat}-${arrowPath[1].lat}-${arrowPath[1].lng}`,
        );
      })
      .filter((arrow): arrow is GoogleMapArrow => !!arrow);
  }

  addMarkers() {
    const scaledSize = new google.maps.Size(20, 30);
    if (this.features.count) {
      this.vertices = this.features.rows.map(
        (row: any) =>
          row.gps.latitude &&
          row.gps.latitude !== 'null' &&
          row.gps.longitude &&
          row.gps.longitude !== 'null' && {
            lat: parseFloat(row.gps.latitude),
            lng: parseFloat(row.gps.longitude),
          },
      );
      this.vertices = this.vertices.filter((vertice: any) => vertice);
      const locations: Array<any> = [];
      this.features.rows.forEach((row: any) => {
        row.gps.latitude &&
          row.gps.latitude !== 'null' &&
          row.gps.longitude &&
          row.gps.longitude !== 'null' &&
          locations.push({
            position: new google.maps.LatLng(
              parseFloat(row.gps.latitude),
              parseFloat(row.gps.longitude),
            ),
          });
      });
      const markedPoints = {
        url: `${this.iconPath}svg/green-circle-icon.svg`,
        anchor: new google.maps.Point(6, 6),
        scaledSize: new google.maps.Size(11, 11),
      };
      locations.forEach((feature) => {
        this.markers.push(
          new google.maps.Marker({
            position: feature.position,
            icon: markedPoints,
          }),
        );
      });
      if (this.markers.length) {
        this.markers[0].icon = {
          url: `${this.iconPath}png/blue-marker.png`,
          scaledSize,
        };
        this.markers[this.markers.length - 1].icon = {
          url: `${this.iconPath}png/red-marker.png`,
          scaledSize,
        };
        if (this.customMarkers.indexes.length) {
          this.customMarkers.indexes.forEach((index: number) => {
            this.markers[index].icon = {
              url: this.customMarkers.icon,
              scaledSize,
            };
          });
        }
        const firstLat = this.features.rows.find((row: any) => row.gps.latitude)
          .gps.latitude;
        const firstLng = this.features.rows.find(
          (row: any) => row.gps.longitude,
        ).gps.longitude;
        this.center = {
          lat: parseFloat(
            this.features.rows.find((row: any) => row.isEmergencyState)?.gps
              .latitude ?? firstLat,
          ),
          lng: parseFloat(
            this.features.rows.find((row: any) => row.isEmergencyState)?.gps
              .longitude ?? firstLng,
          ),
        };
      }
    }
  }

  /**
   * Dual/multi-party mode: plots each party's latest known GPS point as a dot+label marker, adds
   * a velocity arrow showing where they're heading and how fast (FR-402), and, when exactly two
   * parties resolve to a position, draws a connecting line so an admin can see both Perpetrator
   * and Protected Party (Survivor) together on one map.
   */
  addPartyMarkers() {
    const positions: google.maps.LatLngLiteral[] = [];

    (this.parties ?? []).forEach((party) => {
      const track = this.trackOf(party.features);
      if (!track.length) {
        return;
      }
      const latest = track[track.length - 1];
      const color = party.color ?? DEFAULT_ARROW_COLOR;
      // Name chip on the side opposite travel so it doesn't cover the velocity arrow.
      const labelOnLeft = this.labelShouldSitLeft(track);
      const isDraggable = this.markersDraggable;
      this.markers.push({
        position: { lat: latest.lat, lng: latest.lng },
        title: party.label,
        partyType: party.partyType ?? (party.label.toLowerCase().includes('survivor') ? 'SURVIVOR' : 'PERPETRATOR'),
        icon: this.buildDotMarkerIcon(party.label, color, labelOnLeft),
        options: {
          draggable: isDraggable,
          cursor: isDraggable ? 'grab' : undefined,
        },
      });

      this.partyArrowSources.push({ track, color: party.color });

      if (track.length > 1) {
        this.trails.push(...this.buildTrail(track, party.color));
      }

      positions.push(latest);
    });

    this.vertices = positions.length > 1 ? positions : [];

    if (positions.length) {
      const latSum = positions.reduce((sum, point) => sum + point.lat, 0);
      const lngSum = positions.reduce((sum, point) => sum + point.lng, 0);
      this.center = {
        lat: latSum / positions.length,
        lng: lngSum / positions.length,
      };
    }

    this.rebuildArrows();
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent, marker: any): void {
    if (!event?.latLng) {
      return;
    }
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    marker.position = { lat, lng };
    this.partyDragEnd.emit({
      partyType: marker.partyType,
      partyLabel: marker.title,
      lat,
      lng,
    });
  }

  /**
   * Put the name chip on the side opposite the travel direction so it doesn't cover the arrow.
   * Eastward → chip left; westward / N–S / unknown → chip right (default).
   */
  private labelShouldSitLeft(track: GeoPoint[]): boolean {
    if (track.length < 2) {
      return false;
    }
    const end = track[track.length - 1];
    const start =
      track.length >= 3 ? track[track.length - 3] : track[track.length - 2];
    const dLng = end.lng - start.lng;
    const dLat = end.lat - start.lat;
    if (Math.abs(dLng) >= Math.abs(dLat)) {
      return dLng > 0;
    }
    return false;
  }

  /** Usable GPS points for a party, oldest first. */
  private trackOf(features: any): GeoPoint[] {
    return (features?.rows ?? [])
      .filter(
        (row: any) =>
          row?.gps?.latitude &&
          row.gps.latitude !== 'null' &&
          row?.gps?.longitude &&
          row.gps.longitude !== 'null',
      )
      .map((row: any) => ({
        lat: parseFloat(row.gps.latitude),
        lng: parseFloat(row.gps.longitude),
      }));
  }

  /**
   * A small coloured dot (matching the party's own colour) with a dark label chip beside it,
   * built as a plain `google.maps.Icon` (SVG data URI) so it stays on the legacy `Marker`
   * pipeline — `AdvancedMarkerElement` custom content would work too, but it requires a Map ID,
   * and Google Maps silently ignores the `styles` JSON array (this map's dark theme) whenever a
   * Map ID is set. The dot is centred on the actual coordinate via `anchor`; the label chip sits
   * opposite the velocity arrow when heading is known (`labelOnLeft`).
   */
  private buildDotMarkerIcon(
    label: string,
    color: string,
    labelOnLeft = false,
  ): google.maps.Icon {
    const dotDiameter = DOT_MARKER_DIAMETER_PX;
    const dotRadius = dotDiameter / 2;
    const chipHeight = DOT_MARKER_LABEL_HEIGHT_PX;
    const text = label.toUpperCase();
    const chipWidth = Math.max(
      20,
      text.length * DOT_MARKER_CHAR_WIDTH_PX + DOT_MARKER_CHIP_PADDING_X_PX * 2,
    );
    const gap = 3;
    const totalWidth = dotDiameter + gap + chipWidth + 4;
    const totalHeight = Math.max(dotDiameter + 4, chipHeight + 4);
    const anchorX = labelOnLeft
      ? chipWidth + gap + dotRadius + 2
      : dotRadius + 2;
    const anchorY = dotRadius + 2;
    const chipX = labelOnLeft ? 2 : anchorX + dotRadius + gap;
    const circleX = anchorX;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}">
        <circle cx="${circleX}" cy="${anchorY}" r="${dotRadius}" fill="${color}" stroke="#ffffff" stroke-width="2"/>
        <rect x="${chipX}" y="${anchorY - chipHeight / 2}" width="${chipWidth}" height="${chipHeight}" rx="3" fill="rgba(0,0,0,0.75)"/>
        <text x="${chipX + DOT_MARKER_CHIP_PADDING_X_PX}" y="${anchorY + 3}" fill="#ffffff" font-family="Poppins, Arial, sans-serif" font-size="9" font-weight="600" letter-spacing="0.3">${this.escapeXml(text)}</text>
      </svg>
    `.trim();

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      anchor: new google.maps.Point(anchorX, anchorY),
      scaledSize: new google.maps.Size(totalWidth, totalHeight),
    };
  }

  private escapeXml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Ground resolution at the live map zoom / latitude. Used to convert pixel-constant arrow
   * lengths into geographic polyline meters so on-screen size is stable across zoom.
   */
  private arrowMetersPerPixel(): number {
    const zoom = this.mapRef?.googleMap?.getZoom() ?? this.zoom;
    const lat =
      this.mapRef?.googleMap?.getCenter()?.lat() ??
      this.center?.lat ??
      this.zones?.center?.lat;
    if (zoom == null || lat == null || Number.isNaN(lat)) {
      return ARROW_FALLBACK_METERS_PER_PIXEL;
    }
    const metersPerPixel = metersPerPixelAt(lat, zoom);
    if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) {
      return ARROW_FALLBACK_METERS_PER_PIXEL;
    }
    return metersPerPixel;
  }

  /** White casing under a bold coloured line, so the arrow reads over any basemap. */
  private buildArrow(
    path: GeoPoint[],
    color?: string,
    id: string = 'arrow',
  ): GoogleMapArrow {
    const stroke = color ?? DEFAULT_ARROW_COLOR;
    const latLngPath = path as google.maps.LatLngLiteral[];
    // Include `path` inside options — MapPolyline's options watcher calls setOptions()
    // without the separate [path] input, which can leave the line empty on reuse.
    return {
      id,
      path,
      // Thinner strokes keep the tip/barbs sharp; white casing stays just wide enough for contrast.
      casingOptions: {
        path: latLngPath,
        strokeColor: '#ffffff',
        strokeOpacity: 0.95,
        strokeWeight: 4,
        clickable: false,
        zIndex: 40,
        geodesic: false,
      },
      lineOptions: {
        path: latLngPath,
        strokeColor: stroke,
        strokeOpacity: 1,
        strokeWeight: 2,
        clickable: false,
        zIndex: 41,
        geodesic: false,
      },
    };
  }

  /**
   * A party's recent-movement trail, through their known history (real API data for either
   * party — this isn't mock-specific, so a moving Protected Party gets one too). Built as one
   * short polyline segment per consecutive pair of points, fading from faint (oldest) to more
   * visible (most recent, right where it meets the live marker/arrow) — a flat single-opacity
   * line left it ambiguous which end was recent, especially wherever the path doubled back near
   * itself. Capped to the most recent points so a long real history doesn't render hundreds of
   * segments.
   */
  private buildTrail(track: GeoPoint[], color?: string): GoogleMapTrail[] {
    const stroke = color ?? DEFAULT_TRAIL_COLOR;
    const points = track.slice(-TRAIL_MAX_POINTS);
    const segmentCount = points.length - 1;

    return points.slice(1).map((point, index) => {
      const progress = segmentCount > 1 ? index / (segmentCount - 1) : 1;
      const opacity =
        TRAIL_MIN_OPACITY + (TRAIL_MAX_OPACITY - TRAIL_MIN_OPACITY) * progress;
      return {
        path: [points[index], point],
        options: {
          strokeColor: stroke,
          strokeOpacity: opacity,
          strokeWeight: 2,
          clickable: false,
          zIndex: 5,
        },
      };
    });
  }

  /**
   * Frame everything that matters — both parties and the outer zone ring — instead of trusting a
   * fixed zoom, which strands one party off-screen when the pair is far apart. Only ever runs
   * once, and only after the container has a real size: `fitBounds` on a zero-size map (this one
   * lives in a sidenav that animates open) lands at world zoom.
   *
   * After the fit settles, clamp zoom so it never goes *closer* than `@Input() zoom` (pairs that
   * sit inside a small zone would otherwise fill the viewport). Far-apart pairs still zoom out.
   */
  private tryFitBounds() {
    // Scoped to dual-party mode: single-user mode already computes its own center/zoom from the
    // emergency-location logic in `addMarkers()` and shouldn't be second-guessed here.
    if (!this.parties?.length || this.hasFitted || !this.mapRef?.googleMap) {
      return;
    }
    const map = this.mapRef.googleMap;
    const div = map.getDiv() as HTMLElement | undefined;
    if (!div?.clientWidth || !div?.clientHeight) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let hasPoint = false;

    this.markers.forEach((marker) => {
      if (marker.position) {
        bounds.extend(marker.position);
        hasPoint = true;
      }
    });

    if (this.zones) {
      const { center, yellowRadiusMeters, whiteRadiusMeters } = this.zones;
      const outerRadius = whiteRadiusMeters ?? yellowRadiusMeters;
      [0, 90, 180, 270].forEach((bearing) => {
        bounds.extend(destinationPoint(center, outerRadius, bearing));
      });
      hasPoint = true;
    }

    if (!hasPoint) {
      return;
    }

    this.mapRef.fitBounds(bounds, this.fitPaddingPx);
    this.hasFitted = true;
    this.bindZoomListener();

    const maxZoom = this.zoom;
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const fittedZoom = map.getZoom();
      if (fittedZoom != null && fittedZoom > maxZoom) {
        map.setZoom(maxZoom);
      }
      // fitBounds / the clamp above change zoom outside Angular — rescale arrows once settled.
      this.ngZone.run(() => this.rebuildArrows());
    });
  }
}
